import { forwardRef } from "react";
import { cn } from "../lib/cn";

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "text" | "rectangular" | "rounded" | "circular";
  width?: number | string;
  height?: number | string;
  animation?: "pulse" | "wave" | false;
}

type SkeletonVariant = NonNullable<SkeletonProps["variant"]>;

const variantClasses: Record<SkeletonVariant, string> = {
  // Overrides the base rounded-lg (twMerge: last radius class wins).
  text: "h-4 w-full rounded",
  rectangular: "",
  rounded: "",
  // 40px default (MUI parity); width/height props or className override it.
  circular: "size-10 rounded-full",
};

/**
 * Loading placeholder over the `surface-disabled` token. `wave` currently
 * renders as `pulse`; `animation={false}` disables it.
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  (
    { variant = "text", width, height, animation = "pulse", className, style, ...props },
    ref,
  ) => {
    const dimensionStyle: React.CSSProperties = {};
    if (width !== undefined) {
      dimensionStyle.width = width;
    }
    if (height !== undefined) {
      dimensionStyle.height = height;
    }

    return (
      <div
        ref={ref}
        aria-hidden="true"
        className={cn(
          "bg-surface-disabled rounded-lg",
          animation !== false && "animate-pulse",
          variantClasses[variant],
          className,
        )}
        style={{ ...dimensionStyle, ...style }}
        {...props}
      />
    );
  },
);
Skeleton.displayName = "Skeleton";
